<template>
  <v-dialog
    v-model="isVisible"
    :max-width="600"
    scrim="transparent"
    persistent
  >
    <v-card
      data-cy="resource-dialog"
      border
      flat
      tile
    >
      <v-card-title>
        <v-row
          class="d-flex align-center"
          dense
        >
          <v-col :cols="closeable ? 10 : 12">
            <div>
              {{ $bct('Multi-factor authentication enrollment') }}
            </div>
          </v-col>
          <v-col
            v-if="closeable"
            class="ta-right"
          >
            <v-btn
              @click="close"
              :aria-label="$bct('Close')"
              icon="close"
              size="x-small"
              variant="text"
            />
          </v-col>
        </v-row>
      </v-card-title>

      <v-divider />

      <v-card-text>
        <v-row>
          <v-col>
            <p>{{ $bct('1. Enter the number of your SMS enabled device:') }}</p>
          </v-col>
        </v-row>

        <v-row dense>
          <v-col>
            <MaskedInput
              v-model="phone"
              v-slot="{ inputRef, masked }"
              mask="(###) ###-####"
            >
              <!-- eslint-disable-next-line prefer-standard-components/no-vuetify-inputs -- TODO migrate to LabeledTextfield -->
              <v-text-field
                v-model="masked.value"
                :ref="inputRef"
                :aria-label="$bct('Phone')"
                variant="filled"
                hide-details
              />
            </MaskedInput>
          </v-col>
        </v-row>

        <v-row>
          <v-col>
            <v-btn
              @click="enroll"
              :disabled="invalidPhone || !!challenge"
              :loading="processing"
              color="primary"
            >
              {{ $bct('Send one-time passcode') }}
            </v-btn>
          </v-col>
        </v-row>

        <v-divider class="my-6" />

        <v-row dense>
          <v-col>
            <p>
              {{ $bct('2. Enter the one-time passcode sent to your device:') }}
            </p>
          </v-col>
        </v-row>

        <v-row dense>
          <v-col>
            <v-otp-input
              v-model="code"
              @finish="verify"
              :disabled="!challenge || processing"
              class="mxw-400"
              data-cy="otp-input"
              length="6"
            />
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';

import MaskedInput from '@/shared/components/form/MaskedInput.vue';
import { trimOtp } from '@/shared/utils/otp.js';
import useApi from '@/shared/composables/useApi';
import useRouterHelper from '@/shared/composables/useRouterHelper';

import useEventBus from '../composables/useEventBus';

const { api } = useApi();
const eventBus = useEventBus();
const { updateQuery } = useRouterHelper();
const route = useRoute();
const router = useRouter();
const store = useStore();
const $bct = inject('translator');

const props = defineProps({
  closeable: {
    type: Boolean,
    default: false,
  },
  eager: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['change']);

const code = ref('');
const isVisible = ref(false);
const phone = ref('');
const processing = ref(false);
const challenge = ref(null);

const invalidPhone = computed(() => {
  return phone.value?.length !== 14;
});

watch(code, (newValue) => trimOtp(code, newValue));

watch(
  () => route.query.action,
  async (newVal) => {
    if (newVal === 'mfa') open();
    else if (props.closeable) isVisible.value = false;
    else await router.push({ query: { action: 'mfa' } });
  },
  { immediate: true },
);

onMounted(async () => {
  if (!props.eager) return;
  await updateQuery({ action: 'mfa' });
});

async function close() {
  isVisible.value = false;
  code.value = '';
  phone.value = '';
  challenge.value = null;
  await updateQuery({ action: null });
}

async function enroll() {
  processing.value = true;
  const resp = await api.member.factor
    .create({ type: 'sms', phone: phone.value.replace(/[^0-9]/g, '') })
    .catch(handleEnrollError);

  processing.value = false;
  if (resp.data) {
    eventBus.chime('One-time passcode sent to your device.');
    challenge.value = resp.data;
  }
}

function handleEnrollError(error) {
  processing.value = false;
  eventBus.error(error);
}

function handleVerifyError(error) {
  processing.value = false;
  code.value = null;
  eventBus.error(error);
}

function open() {
  isVisible.value = true;
}

async function verify() {
  processing.value = true;
  const resp = await api.member.factor
    .update({
      code: code.value,
      type: 'sms',
      challenge_id: challenge.value.challenge_id,
    })
    .catch(handleVerifyError);

  if (resp?.status !== 200) return;

  store.dispatch('identify', {
    success(profile) {
      if (profile.workos_mfa_factor_confirmed) {
        processing.value = false;
        void close();
        eventBus.chime('MFA Activated');
        emit('change');
      }
    },
  });
}
</script>
